import {
  ChatMessage,
  Conversation,
  ConversationRequest,
  CosmosDBHealth,
  CosmosDBStatus,
  FrontendSettings,
} from './models';
import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';

/**
 * Clean citation JSON from message content
 * Azure OpenAI sometimes embeds citation data in the response text
 */
const cleanMessageContent = (content: string): string => {
  // Remove citation JSON objects that look like: {"citations":[...]}
  let cleaned = content.replace(/\{"citations":\[.*?\]\}/gs, '');
  
  // Remove standalone citation objects that look like: {"content":"...","title":"..."}
  cleaned = cleaned.replace(/\{"content":".*?","title":".*?".*?\}/gs, '');
  
  // Clean up any extra whitespace or newlines left behind
  cleaned = cleaned.replace(/\n\s*\n\s*\n/g, '\n\n').trim();
  
  return cleaned;
};

export async function conversationApi(
  options: ConversationRequest,
  abortSignal: AbortSignal,
  discoveryApi: DiscoveryApi,
  fetchApi: FetchApi,
): Promise<Response> {
  const baseUrl = await discoveryApi.getBaseUrl('ai-assistant');
  const response = await fetchApi.fetch(`${baseUrl}/conversation`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      messages: options.messages,
    }),
    signal: abortSignal,
  });

  return response;
}

export async function getUserInfo(): Promise<boolean> {
  // In Backstage, authentication is handled by the identity API
  // We just need to verify the user is authenticated
  return true;
}

export const historyList = async (
  discoveryApi: DiscoveryApi,
  fetchApi: FetchApi,
  offset = 0,
): Promise<Conversation[] | null> => {
  try {
    const baseUrl = await discoveryApi.getBaseUrl('ai-assistant');
    const response = await fetchApi.fetch(`${baseUrl}/history/list?offset=${offset}`, {
      method: 'GET',
    });
    
    if (!response.ok) {
      console.error('There was an issue fetching your history list.', response.statusText);
      return null;
    }

    const payload = await response.json();
    console.log('response of /history/list?offset=', payload);
    if (!Array.isArray(payload)) {
      console.error('There was an issue fetching your data.');
      return null;
    }
    
    return payload;
  } catch (err) {
    console.error('There was an issue fetching your data.', err);
    return null;
  }
};

export const historyRead = async (
  discoveryApi: DiscoveryApi,
  fetchApi: FetchApi,
  convId: string,
): Promise<ChatMessage[]> => {
  try {
    const baseUrl = await discoveryApi.getBaseUrl('ai-assistant');
    const response = await fetchApi.fetch(`${baseUrl}/history/read`, {
      method: 'POST',
      body: JSON.stringify({
        conversation_id: convId,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      return [];
    }
    
    const payload = await response.json();
    const messages: ChatMessage[] = [];
    if (payload?.messages) {
      payload.messages.forEach((msg: any) => {
        const message: ChatMessage = {
          id: msg.id,
          role: msg.role,
          date: msg.createdAt,
          content: msg.role === 'assistant' ? cleanMessageContent(msg.content) : msg.content,
          feedback: msg.feedback ?? undefined,
        };
        // Include citations if present (for assistant messages)
        if (msg.citations) {
          (message as any).citations = msg.citations;
        }
        messages.push(message);
      });
    }
    return messages;
  } catch (err) {
    console.error('There was an issue fetching your data.', err);
    return [];
  }
};

export const historyGenerate = async (
  discoveryApi: DiscoveryApi,
  fetchApi: FetchApi,
  options: ConversationRequest,
  abortSignal: AbortSignal,
  convId?: string,
): Promise<Response> => {
  let body;
  if (convId) {
    body = JSON.stringify({
      conversation_id: convId,
      messages: options.messages,
    });
  } else {
    body = JSON.stringify({
      messages: options.messages,
    });
  }
  
  try {
    const baseUrl = await discoveryApi.getBaseUrl('ai-assistant');
    const response = await fetchApi.fetch(`${baseUrl}/history/generate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: body,
      signal: abortSignal,
    });
    return response;
  } catch (err) {
    console.error('There was an issue fetching your data.', err);
    return new Response();
  }
};

export const historyUpdate = async (
  discoveryApi: DiscoveryApi,
  fetchApi: FetchApi,
  messages: ChatMessage[],
  convId: string,
): Promise<Response> => {
  try {
    const baseUrl = await discoveryApi.getBaseUrl('ai-assistant');
    const response = await fetchApi.fetch(`${baseUrl}/history/update`, {
      method: 'POST',
      body: JSON.stringify({
        conversation_id: convId,
        messages: messages,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response;
  } catch (err) {
    console.error('There was an issue fetching your data.', err);
    const errRes: Response = {
      ...new Response(),
      ok: false,
      status: 500,
    };
    return errRes;
  }
};

export const historyDelete = async (
  discoveryApi: DiscoveryApi,
  fetchApi: FetchApi,
  convId: string,
): Promise<Response> => {
  try {
    const baseUrl = await discoveryApi.getBaseUrl('ai-assistant');
    const response = await fetchApi.fetch(`${baseUrl}/history/delete`, {
      method: 'DELETE',
      body: JSON.stringify({
        conversation_id: convId,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response;
  } catch (err) {
    console.error('There was an issue fetching your data.', err);
    const errRes: Response = {
      ...new Response(),
      ok: false,
      status: 500,
    };
    return errRes;
  }
};

export const historyDeleteAll = async (
  discoveryApi: DiscoveryApi,
  fetchApi: FetchApi,
): Promise<Response> => {
  try {
    const baseUrl = await discoveryApi.getBaseUrl('ai-assistant');
    const response = await fetchApi.fetch(`${baseUrl}/history/delete_all`, {
      method: 'DELETE',
      body: JSON.stringify({}),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response;
  } catch (err) {
    console.error('There was an issue fetching your data.', err);
    const errRes: Response = {
      ...new Response(),
      ok: false,
      status: 500,
    };
    return errRes;
  }
};

export const historyClear = async (
  discoveryApi: DiscoveryApi,
  fetchApi: FetchApi,
  convId: string,
): Promise<Response> => {
  try {
    const baseUrl = await discoveryApi.getBaseUrl('ai-assistant');
    const response = await fetchApi.fetch(`${baseUrl}/history/clear`, {
      method: 'POST',
      body: JSON.stringify({
        conversation_id: convId,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response;
  } catch (err) {
    console.error('There was an issue fetching your data.', err);
    const errRes: Response = {
      ...new Response(),
      ok: false,
      status: 500,
    };
    return errRes;
  }
};

export const historyRename = async (
  discoveryApi: DiscoveryApi,
  fetchApi: FetchApi,
  convId: string,
  title: string,
): Promise<Response> => {
  try {
    const baseUrl = await discoveryApi.getBaseUrl('ai-assistant');
    const response = await fetchApi.fetch(`${baseUrl}/history/rename`, {
      method: 'POST',
      body: JSON.stringify({
        conversation_id: convId,
        title: title,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response;
  } catch (err) {
    console.error('There was an issue fetching your data.', err);
    const errRes: Response = {
      ...new Response(),
      ok: false,
      status: 500,
    };
    return errRes;
  }
};

export const historyEnsure = async (
  discoveryApi: DiscoveryApi,
  fetchApi: FetchApi,
): Promise<CosmosDBHealth> => {
  try {
    const baseUrl = await discoveryApi.getBaseUrl('ai-assistant');
    const response = await fetchApi.fetch(`${baseUrl}/history/ensure`, {
      method: 'GET',
    });
    
    const respJson = await response.json();
    let formattedResponse;
    if (respJson.message) {
      formattedResponse = CosmosDBStatus.Working + ' ' + respJson.message;
    } else {
      if (response.status === 500) {
        formattedResponse = CosmosDBStatus.NotWorking;
      } else if (response.status === 401) {
        formattedResponse = CosmosDBStatus.InvalidCredentials;
      } else if (response.status === 422) {
        formattedResponse = respJson.error;
      } else {
        formattedResponse = CosmosDBStatus.NotConfigured;
      }
    }
    if (!response.ok) {
      return {
        cosmosDB: false,
        status: formattedResponse,
      };
    } else {
      return {
        cosmosDB: true,
        status: formattedResponse,
      };
    }
  } catch (err) {
    console.error('There was an issue fetching your data.', err);
    return {
      cosmosDB: false,
      status: err as string,
    };
  }
};

export const frontendSettings = async (
  discoveryApi: DiscoveryApi,
  fetchApi: FetchApi,
): Promise<FrontendSettings | null> => {
  try {
    const baseUrl = await discoveryApi.getBaseUrl('ai-assistant');
    const response = await fetchApi.fetch(`${baseUrl}/frontend_settings`, {
      method: 'GET',
    });
    return await response.json();
  } catch (err) {
    console.error('There was an issue fetching your data.', err);
    return null;
  }
};

export const historyMessageFeedback = async (
  discoveryApi: DiscoveryApi,
  fetchApi: FetchApi,
  messageId: string,
  feedback: string,
): Promise<Response> => {
  try {
    const baseUrl = await discoveryApi.getBaseUrl('ai-assistant');
    const response = await fetchApi.fetch(`${baseUrl}/history/message_feedback`, {
      method: 'POST',
      body: JSON.stringify({
        message_id: messageId,
        message_feedback: feedback,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response;
  } catch (err) {
    console.error('There was an issue logging feedback.', err);
    const errRes: Response = {
      ...new Response(),
      ok: false,
      status: 500,
    };
    return errRes;
  }
};

